/*
 * This is a simplified way to declare a component wizard. The wizard definition is created as a simple javascript
 * object and will then be converted to a ComponentWizard instance.
 */

import type { WizardComponentRenderer } from "./wizard"

export interface DeclarativeWizardQuestion<Result> {
    /** The follow up question */
    question: string

    /** The options for the new question */
    options: DeclarativeWizardAnswer<Result>[]
}

export type ActionType<Result> = Result | DeclarativeWizardQuestion<Result> | WizardComponentRenderer<Result>

export interface DeclarativeWizardAnswer<Result> {
    /** The answer to the question */
    answer: string

    /** The result */
    action: ActionType<Result>

    /** Whether this answer or question should be available */
    if?: boolean

    /** Whether this answer is shown but disabled */
    disabled?: boolean
}

/**
 * Resolve the wizard definition by removing all options that are not available.
 */
export function resolveDeclarativeWizard<T>(definition: DeclarativeWizardQuestion<T>) {
    let newDefinition: DeclarativeWizardQuestion<T> = definition

    newDefinition = walkConditions(newDefinition)
    newDefinition = walkEmptyOptions(newDefinition)!

    return newDefinition
}

/**
 * Visit every question in the given tree and apply the visitor function to it. If the visitor function returns null,
 * the node and all its children will be removed from the tree.
 */
export function visitQuestions<T>(
    definition: DeclarativeWizardQuestion<T>,
    visitor: (question: DeclarativeWizardQuestion<T>) => DeclarativeWizardQuestion<T> | null,
): DeclarativeWizardQuestion<T> | null {
    const newDefinition = visitor(definition)

    if (!newDefinition) {
        return null
    }

    const newOptions = newDefinition.options
        .map((option): DeclarativeWizardAnswer<T> | null => {
            if (isDeclarativeWizardQuestion(option.action)) {
                const action = visitQuestions(option.action, visitor)

                if (!action) {
                    return null
                }

                return {
                    ...option,
                    action,
                }
            }

            return option
        })
        .filter(option => !!option)
        .map(option => option as DeclarativeWizardAnswer<T>)

    return {
        ...newDefinition,
        options: newOptions,
    }
}

/**
 * Visit every answer in the given tree and apply the visitor function to it. If the visitor function returns null,
 * the node and all its children will be removed from the tree.
 */
export function visitAnswers<T>(
    definition: DeclarativeWizardQuestion<T>,
    visitor: (answer: DeclarativeWizardAnswer<T>) => DeclarativeWizardAnswer<T> | null,
): DeclarativeWizardQuestion<T> {
    return visitQuestions(definition, question => {
        return {
            question: question.question,
            options: question.options
                .map((option): DeclarativeWizardAnswer<T> | null => {
                    const answer = visitor(option)

                    if (!answer) {
                        return null
                    }

                    return answer
                })
                .filter(option => !!option)
                .map(option => option as DeclarativeWizardAnswer<T>),
        }
    })!
}

/**
 * Resolve all "if" conditions in the wizard definition and remove the options that evaluate to false.
 */
export function walkConditions<T>(definition: DeclarativeWizardQuestion<T>): DeclarativeWizardQuestion<T> {
    return visitAnswers(definition, answer => {
        if ("if" in answer && !answer.if) {
            return null
        }

        const result = { ...answer }
        delete result.if

        return result
    })
}

/**
 * Recursively walk through the wizard definition and remove all questions that have no options.
 */
export function walkEmptyOptions<T>(definition: DeclarativeWizardQuestion<T>): DeclarativeWizardQuestion<T> | null {
    // filter all questions that have no options
    const questions = visitQuestions(definition, question => {
        if (question.options.length === 0) {
            return null
        }

        return question
    })

    // filter answers that have a follow up question with no options
    return visitAnswers(questions!, answer => {
        const action = answer.action
        if (isDeclarativeWizardQuestion(action) && action.options.length === 0) {
            return null
        }

        return answer
    })
}

export function isDeclarativeWizardQuestion<T>(action: ActionType<T>): action is DeclarativeWizardQuestion<T> {
    return action && typeof action === "object" && "question" in action
}
